// External libraries
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AddAlt, CaretRight, ChevronDown, ChevronUp, Close, Search } from "@carbon/icons-react";
import { useRecoilState, useRecoilValue } from "recoil";

// Components
import { Typography } from "../Shadcn/Typography";
import { Button } from "../Shadcn/Button";
import { Checkbox } from "../Shadcn/Checkbox";
import { Slider } from "../Shadcn/Slider";
import { MarkerFilterModal } from "./MarkerFilterModal";
import {
    markerFiltersPayloadState, markerFilterDataState, markerFilterSlidersState,
    POICategories, selMarkerCategoriesState, selPOICategories,
} from "../../states";

const safeRange = (min: number, max: number, rMin: number, rMax: number): [number, number] => {
    if (rMin > max || rMax < min) return [min, max];
    const sMin = Math.max(min, rMin);
    const sMax = Math.min(max, rMax);
    if (sMin > sMax) return [min, max];
    return [sMin, sMax];
};

const LS_KEY = 'markerFilterSliders';
const readFromLS = (): Record<string, Record<string, { rangeMin: number; rangeMax: number }>> => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) ?? '{}'); } catch { return {}; }
};

const timestampToDateString = (ts?: number): string => {
    if (!ts || isNaN(ts)) return '1970-01-01';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '1970-01-01';
    return d.toISOString().split('T')[0];
};

interface POICategoryListHomeProps {
    isOpen: boolean;
    handleListOpen: (keyName: string, tagName?: string, buttonEl?: HTMLElement | null) => void;
    onCloseFilterPopup: () => void;
    catCheckBoxRef: React.RefObject<HTMLDivElement>;
    buttonRef?: React.RefObject<HTMLDivElement>;
    isListOpen: { categories: boolean; brands: boolean; aggLayer?: boolean };
    catchment?: string | null;
    isAggLayerOff?: boolean;
    layer?: any;
}

function getAllSelected(cats: any[]) {
    return {
        category: cats.map((c: any) => c.key),
        subcategory: cats.flatMap((c: any) => (c.sub_categories ?? []).map((s: any) => s.key)),
    };
}

export const POICategoryListHome = ({
    isOpen,
    handleListOpen,
    onCloseFilterPopup,
    catCheckBoxRef,
    buttonRef,
    isListOpen,
    isAggLayerOff,
    layer,
}: POICategoryListHomeProps) => {
    const localButtonRef = useRef<HTMLDivElement | null>(null);
    const poiCategories = useRecoilValue(POICategories);
    const markerFilterData = useRecoilValue(markerFilterDataState);
    const [selCategoryList, setSelCategoryList] = useRecoilState(selPOICategories);
    const [selMarkerCategories, setSelMarkerCategories] = useRecoilState(selMarkerCategoriesState);
    const [markerFilterSliders, setMarkerFilterSliders] = useRecoilState(markerFilterSlidersState);
    const [markerFiltersPayload, setMarkerFiltersPayload] = useRecoilState<any[]>(markerFiltersPayloadState);

    const isPoiMarker = layer?.markertype === "poi";
    const isPoiType = isPoiMarker || layer?.type === "PoI";
    const layerKey = layer?.type ?? "poimarker";
    const categoriesKey = isPoiType ? "PoI" : layerKey;

    // ── Categories ────────────────────────────────────────────────────────────
    const layerCategories: any[] = useMemo(() => {
        const raw = markerFilterData[categoriesKey]?.categories;
        if (raw?.length) {
            return raw.map((c: any, i: number) =>
                c.key !== undefined ? c : { key: i, name: c.name, sub_categories: c.sub_categories ?? [] }
            );
        }
        if (categoriesKey === "PoI") return poiCategories;
        return [];
    }, [markerFilterData, categoriesKey, poiCategories]);

    const committedSelection = isPoiType
        ? selCategoryList
        : (selMarkerCategories[layerKey] ?? getAllSelected(layerCategories));

    const [selTempCats, setSelTempCats] = useState(committedSelection);
    const [expandedKeys, setExpandedKeys] = useState<number[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showMarkerFilterModal, setShowMarkerFilterModal] = useState(false);

    // ── Filters / sliders ─────────────────────────────────────────────────────
    const layerFilters: any[] = markerFilterData[layerKey]?.filters ?? [];
    const taggedFilters = useMemo(() => layerFilters.filter((f: any) => f.is_tagged), [layerFilters]);
    const taggedFilterKeys = useMemo(() => taggedFilters.map((f: any) => f.key).join(','), [taggedFilters]);
    const filteredTaggedFilters = useMemo(() => {
        if (!searchTerm.trim()) return taggedFilters;
        const q = searchTerm.toLowerCase();
        return taggedFilters.filter((f: any) => (f.overlay ?? '').toLowerCase().includes(q));
    }, [taggedFilters, searchTerm]);

    const [tempSliderRanges, setTempSliderRanges] = useState<Record<string, [number, number]>>({});
    const [tempChecksState, setTempChecksState] = useState<Record<string, string[]>>({});
    const [expandedFilterKeys, setExpandedFilterKeys] = useState<string[]>([]);

    const toggleFilterGroup = (key: string) => {
        setExpandedFilterKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const getFilterCheckState = (filter: any): boolean | 'indeterminate' => {
        if (filter.type === 'checks') {
            const allValues = Array.from(new Set((filter.categories ?? []).map((c: any) => c.value)));
            const selected = tempChecksState[filter.key] ?? allValues;
            if (selected.length === 0) return false;
            if (selected.length === allValues.length) return true;
            return 'indeterminate';
        }
        const range = tempSliderRanges[filter.key] ?? [filter.min, filter.max];
        return range[0] === filter.min && range[1] === filter.max;
    };

    const handleFilterHeaderToggle = (filter: any, checked: boolean) => {
        if (filter.type === 'checks') {
            const allValues = Array.from(new Set((filter.categories ?? []).map((c: any) => c.value)));
            setTempChecksState(prev => ({ ...prev, [filter.key]: checked ? allValues : [] }));
        } else {
            setTempSliderRanges(prev => ({
                ...prev,
                [filter.key]: checked ? [filter.min, filter.max] : [filter.min, filter.min],
            }));
        }
    };

    // Restore committed state from localStorage on mount if Recoil is empty
    useEffect(() => {
        if (!markerFilterSliders[layerKey]) {
            const stored = readFromLS();
            if (stored[layerKey]) {
                setMarkerFilterSliders(prev => ({ ...prev, [layerKey]: stored[layerKey] }));
            }
        }
    }, [layerKey]);

    // Sync slider ranges when tagged filters change — clamp against new bounds
    useEffect(() => {
        setTempSliderRanges(prev => {
            const committed = markerFilterSliders[layerKey] ?? {};
            const updated: Record<string, [number, number]> = {};
            taggedFilters.forEach((f: any) => {
                const cr = committed[f.key];
                if (cr) {
                    // Use committed (Applied) value, clamped to new bounds
                    updated[f.key] = [
                        Math.max(f.min, Math.min(cr.rangeMin, f.max)),
                        Math.max(f.min, Math.min(cr.rangeMax, f.max)),
                    ];
                } else if (prev[f.key] !== undefined) {
                    const [pMin, pMax] = prev[f.key];
                    // Keep in-progress value only if it's still within the new bounds
                    updated[f.key] = (pMin >= f.min && pMax <= f.max)
                        ? prev[f.key]
                        : [f.min, f.max];
                } else {
                    updated[f.key] = [f.min, f.max];
                }
            });
            return updated;
        });
    }, [taggedFilterKeys, layerKey]);

    // ── Category effects ──────────────────────────────────────────────────────
    const filteredCategories: any[] = useMemo(() => {
        if (!searchTerm.trim()) return layerCategories;
        const q = searchTerm.toLowerCase();
        return layerCategories
            .filter((cat: any) =>
                cat.name.toLowerCase().includes(q) ||
                cat.sub_categories?.some((sub: any) => sub.name.toLowerCase().includes(q))
            )
            .map((cat: any) => {
                if (cat.name.toLowerCase().includes(q)) return cat;
                return {
                    ...cat,
                    sub_categories: (cat.sub_categories ?? []).filter((sub: any) =>
                        sub.name.toLowerCase().includes(q)
                    ),
                };
            });
    }, [layerCategories, searchTerm]);

    useEffect(() => {
        setExpandedKeys([-1, ...layerCategories.map((c: any) => c.key)]);
    }, [layerCategories]);

    useEffect(() => {
        setExpandedFilterKeys(taggedFilters.map((f: any) => f.key));
    }, [taggedFilterKeys]);

    useEffect(() => {
        if (searchTerm.trim()) {
            setExpandedKeys([-1, ...layerCategories.map((c: any) => c.key)]);
        }
    }, [searchTerm]);

    // Auto-select all when non-PoI-type categories load for the first time
    useEffect(() => {
        if (!isPoiType && layerCategories.length > 0 && selMarkerCategories[layerKey] === undefined) {
            const allSelected = getAllSelected(layerCategories);
            setSelTempCats(allSelected);
            setSelMarkerCategories(prev => ({ ...prev, [layerKey]: allSelected }));
        }
    }, [layerCategories, isPoiType]);

    useEffect(() => {
        if (isPoiType) setSelTempCats(selCategoryList);
    }, [selCategoryList]);

    useEffect(() => {
        if (!isPoiType && selMarkerCategories[layerKey] !== undefined) {
            setSelTempCats(selMarkerCategories[layerKey]);
        }
    }, [selMarkerCategories[layerKey]]);

    useEffect(() => {
        const committed = markerFilterSliders[layerKey] ?? {};
        const freshRanges = Object.fromEntries(
            taggedFilters.map((f: any) => {
                const cr = committed[f.key];
                return [f.key, cr
                    ? [Math.max(f.min, Math.min(cr.rangeMin, f.max)), Math.max(f.min, Math.min(cr.rangeMax, f.max))]
                    : [f.min, f.max]
                ];
            })
        );
        const freshChecks = Object.fromEntries(
            taggedFilters
                .filter((f: any) => f.type === 'checks')
                .map((f: any) => {
                    const committed = markerFiltersPayload.find((p: any) => p.key === f.key);
                    const allValues = Array.from(new Set((f.categories ?? []).map((c: any) => c.value)));
                    return [f.key, committed?.selected_categories ?? allValues];
                })
        );
        if (isOpen) {
            setTempSliderRanges(freshRanges);
            setTempChecksState(freshChecks);
        } else {
            setSelTempCats(isPoiType ? selCategoryList : (selMarkerCategories[layerKey] ?? getAllSelected(layerCategories)));
            setTempSliderRanges(freshRanges);
            setTempChecksState(freshChecks);
            setSearchTerm('');
            setExpandedKeys([-1, ...layerCategories.map((c: any) => c.key)]);
        }
    }, [isOpen]);

    // ── Checkbox handlers ─────────────────────────────────────────────────────
    const getGroupCheckState = (category: any): boolean | 'indeterminate' => {
        const subKeys = (category.sub_categories ?? []).map((s: any) => s.key);
        if (subKeys.length === 0) return selTempCats.category.includes(category.key);
        const selected = subKeys.filter((k: number) => selTempCats.subcategory.includes(k)).length;
        if (selected === 0) return false;
        if (selected === subKeys.length) return true;
        return 'indeterminate';
    };

    const handleGroupChange = (category: any, checked: boolean) => {
        const subKeys = (category.sub_categories ?? []).map((s: any) => s.key);
        if (subKeys.length === 0) {
            setSelTempCats(prev => ({
                ...prev,
                category: checked
                    ? [...prev.category, category.key]
                    : prev.category.filter((k: number) => k !== category.key),
            }));
        } else {
            setSelTempCats(prev => ({
                category: checked
                    ? [...new Set([...prev.category, category.key])]
                    : prev.category.filter((k: number) => k !== category.key),
                subcategory: checked
                    ? [...new Set([...prev.subcategory, ...subKeys])]
                    : prev.subcategory.filter((k: number) => !subKeys.includes(k)),
            }));
        }
    };

    const handleChildChange = (parentCategory: any, subKey: number, checked: boolean) => {
        const subKeys = (parentCategory.sub_categories ?? []).map((s: any) => s.key);
        const newSubs = checked
            ? [...new Set([...selTempCats.subcategory, subKey])]
            : selTempCats.subcategory.filter((k: number) => k !== subKey);
        const anySubSelected = subKeys.some((k: number) => newSubs.includes(k));
        setSelTempCats(prev => ({
            category: anySubSelected
                ? [...new Set([...prev.category, parentCategory.key])]
                : prev.category.filter((k: number) => k !== parentCategory.key),
            subcategory: newSubs,
        }));
    };

    const toggleGroup = (key: number) => {
        setExpandedKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    // ── Apply / cancel ────────────────────────────────────────────────────────
    const handleCancelFilter = () => {
        setSelTempCats(isPoiType ? selCategoryList : (selMarkerCategories[layerKey] ?? getAllSelected(layerCategories)));
        const committed = markerFilterSliders[layerKey] ?? {};
        setTempSliderRanges(
            Object.fromEntries(
                taggedFilters.map((f: any) => {
                    const cr = committed[f.key];
                    const rMin = cr?.rangeMin ?? f.min;
                    const rMax = cr?.rangeMax ?? f.max;
                    return [f.key, safeRange(f.min, f.max, rMin, rMax)];
                })
            )
        );
        setSearchTerm('');
        onCloseFilterPopup();
    };

    const handleApplyFilter = () => {
        if (isPoiType) {
            setSelCategoryList(selTempCats);
            localStorage.setItem('selCategoryList', JSON.stringify(selTempCats));
        } else {
            setSelMarkerCategories(prev => ({ ...prev, [layerKey]: selTempCats }));
        }

        // Merge tagged slider filter objects into filtersState (same structure as FilterRegionModal)
        const layerFilterKeys = new Set(layerFilters.map((f: any) => f.key));
        const sliderItems = taggedFilters.map((filter: any) => {
            if (filter.type === 'checks') {
                const allValues = Array.from(new Set((filter.categories ?? []).map((c: any) => c.value)));
                return { ...filter, selected_categories: tempChecksState[filter.key] ?? allValues };
            }
            const range = tempSliderRanges[filter.key] ?? [filter.min, filter.max];
            return { ...filter, rangeMin: range[0], rangeMax: range[1] };
        });
        setMarkerFiltersPayload(prev => [
            ...prev.filter((f: any) => !layerFilterKeys.has(f.key)),
            ...sliderItems,
        ]);

        // Commit ranges to markerFilterSlidersState and localStorage
        if (taggedFilters.length > 0) {
            const committedEntry = Object.fromEntries(
                taggedFilters.map((f: any) => {
                    const range = tempSliderRanges[f.key] ?? [f.min, f.max];
                    return [f.key, { rangeMin: range[0], rangeMax: range[1] }];
                })
            );
            setMarkerFilterSliders(prev => ({ ...prev, [layerKey]: committedEntry }));
            const stored = readFromLS();
            localStorage.setItem(LS_KEY, JSON.stringify({ ...stored, [layerKey]: committedEntry }));
        }

        setSearchTerm('');
        onCloseFilterPopup();
    };

    // ── Portal position ───────────────────────────────────────────────────────
    const getPortalStyle = (): React.CSSProperties => {
        if (!localButtonRef.current) return {};
        const rect = localButtonRef.current.getBoundingClientRect();
        const maxPopupHeight = window.innerHeight * 0.85;
        const desiredTop = rect.top - 205;
        const clampedTop = Math.min(desiredTop, window.innerHeight - maxPopupHeight - 8);
        return { left: rect.right + 20, top: Math.max(8, clampedTop) };
    };

    const layerTitle = layer?.title ?? layer?.name ?? 'Markers';

    return (
        <div className="relative w-full">
            {/* trigger row */}
            <div
                ref={(el) => {
                    localButtonRef.current = el;
                    if (buttonRef) (buttonRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                }}
                className="flex items-center justify-between cursor-pointer py-1"
                onClick={(event) => handleListOpen('categories', (event.target as HTMLElement).tagName)}
            >
                <Typography variant="b3" className="text-[var(--ds-solid-neutral-11)] truncate flex-1 min-w-0">Filter</Typography>
                <CaretRight size={12} className="icon-gray-0 p-0 shrink-0" />
            </div>

            {/* filter dialog portal */}
            {isListOpen.categories && localButtonRef.current?.offsetParent !== null &&
                createPortal(
                    <div
                        ref={catCheckBoxRef}
                        className="poi-category-list-home flex flex-col max-h-[85vh]"
                        style={getPortalStyle()}
                    >
                        {/* header */}
                        <div className="flex items-center justify-between border-b px-4 py-2.5 shrink-0">
                            <Typography variant="b2" className="font-medium truncate pr-2">
                                Filter Markers: {layerTitle}
                            </Typography>
                            <button className="cursor-pointer text-foreground shrink-0" onClick={handleCancelFilter}>
                                <Close size={14} />
                            </button>
                        </div>

                        {/* search */}
                        <div className="flex items-center gap-2 px-4 py-3 border-b shrink-0">
                            <Search size={14} className="text-muted-foreground shrink-0" />
                            <input
                                className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground text-foreground"
                                placeholder="Search Category"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* scrollable body: filter groups + tagged filter sliders */}
                        <div className="flex-1 overflow-y-auto no-scrollbar" style={{overflowX: 'hidden'}}>
                        {/* filter groups */}
                        <div>
                            {!isPoiType ? (
                                // Non-POI: all categories nested under a single dummy "Category" parent
                                (!searchTerm.trim() || filteredCategories.length > 0) && <div>
                                    <div
                                        className="flex items-center justify-between px-4 py-2.5 cursor-pointer"
                                        onClick={() => toggleGroup(-1)}
                                    >
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <Checkbox
                                                id={`group-${layerKey}-dummy`}
                                                checked={(() => {
                                                    const allKeys = layerCategories.map((c: any) => c.key);
                                                    const n = allKeys.filter((k: number) => selTempCats.category.includes(k)).length;
                                                    return n === 0 ? false : n === allKeys.length ? true : 'indeterminate';
                                                })()}
                                                onCheckedChange={(val) => {
                                                    const allKeys = layerCategories.map((c: any) => c.key);
                                                    const allSubKeys = layerCategories.flatMap((c: any) =>
                                                        (c.sub_categories ?? []).map((s: any) => s.key)
                                                    );
                                                    setSelTempCats({ category: val ? allKeys : [], subcategory: val ? allSubKeys : [] });
                                                }}
                                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                className="shrink-0"
                                            />
                                            <Typography variant="b2" className="font-medium break-words min-w-0">Category</Typography>
                                        </div>
                                        {expandedKeys.includes(-1)
                                            ? <ChevronUp size={16} className="text-muted-foreground shrink-0" />
                                            : <ChevronDown size={16} className="text-muted-foreground shrink-0" />
                                        }
                                    </div>
                                    {expandedKeys.includes(-1) && filteredCategories.map((category: any) => {
                                        const isExpanded = expandedKeys.includes(category.key);
                                        const groupState = getGroupCheckState(category);
                                        const hasSubs = (category.sub_categories?.length ?? 0) > 0;
                                        return (
                                            <div key={category.key}>
                                                <div
                                                    className="flex items-center justify-between pl-10 pr-4 py-2.5 cursor-pointer"
                                                    onClick={() => hasSubs
                                                        ? toggleGroup(category.key)
                                                        : handleGroupChange(category, !selTempCats.category.includes(category.key))
                                                    }
                                                >
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        <Checkbox
                                                            id={`group-${layerKey}-${category.key}`}
                                                            checked={groupState}
                                                            onCheckedChange={(val) => handleGroupChange(category, !!val)}
                                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                            className="shrink-0"
                                                        />
                                                        <Typography variant="b2" className="text-muted-foreground break-words min-w-0">
                                                            {category.name}{category.count != null ? ` (${category.count})` : ''}
                                                        </Typography>
                                                    </div>
                                                    {hasSubs && (
                                                        isExpanded
                                                            ? <ChevronUp size={16} className="text-muted-foreground shrink-0" />
                                                            : <ChevronDown size={16} className="text-muted-foreground shrink-0" />
                                                    )}
                                                </div>
                                                {isExpanded && category.sub_categories?.map((sub: any) => (
                                                    <div
                                                        key={sub.key}
                                                        className="flex items-center gap-2 pl-[4.5rem] pr-4 py-2 cursor-pointer"
                                                        onClick={() => handleChildChange(category, sub.key, !selTempCats.subcategory.includes(sub.key))}
                                                    >
                                                        <Checkbox
                                                            id={`sub-${layerKey}-${sub.key}`}
                                                            checked={selTempCats.subcategory.includes(sub.key)}
                                                            onCheckedChange={(val) => handleChildChange(category, sub.key, !!val)}
                                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                            className="shrink-0"
                                                        />
                                                        <Typography variant="b2" className="text-muted-foreground break-words min-w-0 flex-1">
                                                            {sub.name}{sub.count != null ? ` (${sub.count})` : ''}
                                                        </Typography>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                // POI: flat list of categories
                                filteredCategories.map((category: any, idx: number) => {
                                    const isExpanded = expandedKeys.includes(category.key);
                                    const groupState = getGroupCheckState(category);
                                    return (
                                        <div key={category.key} className={idx > 0 ? 'border-t' : ''}>
                                            <div
                                                className="flex items-center justify-between px-4 py-2.5 cursor-pointer"
                                                onClick={() => toggleGroup(category.key)}
                                            >
                                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                                    <Checkbox
                                                        id={`group-${layerKey}-${category.key}`}
                                                        checked={groupState}
                                                        onCheckedChange={(val) => handleGroupChange(category, !!val)}
                                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                        className="shrink-0"
                                                    />
                                                    <Typography variant="b2" className="font-medium break-words min-w-0">
                                                        {category.name}{category.count != null ? ` (${category.count})` : ''}
                                                    </Typography>
                                                </div>
                                                {category.sub_categories?.length > 0 && (
                                                    isExpanded
                                                        ? <ChevronUp size={16} className="text-muted-foreground shrink-0" />
                                                        : <ChevronDown size={16} className="text-muted-foreground shrink-0" />
                                                )}
                                            </div>
                                            {isExpanded && category.sub_categories?.map((sub: any) => (
                                                <div
                                                    key={sub.key}
                                                    className="flex items-center gap-2 pl-10 pr-4 py-2 cursor-pointer"
                                                    onClick={() => handleChildChange(
                                                        category,
                                                        sub.key,
                                                        !selTempCats.subcategory.includes(sub.key)
                                                    )}
                                                >
                                                    <Checkbox
                                                        id={`sub-${layerKey}-${sub.key}`}
                                                        checked={selTempCats.subcategory.includes(sub.key)}
                                                        onCheckedChange={(val) => handleChildChange(category, sub.key, !!val)}
                                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                        className="shrink-0"
                                                    />
                                                    <Typography variant="b2" className="text-muted-foreground break-words min-w-0 flex-1">
                                                        {sub.name}{sub.count != null ? ` (${sub.count})` : ''}
                                                    </Typography>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* tagged filter sliders / date filters */}
                        {filteredTaggedFilters.length > 0 && (
                            <div className="border-t px-4 pt-3 pb-1 flex flex-col gap-3">
                                {filteredTaggedFilters.map((filter: any) => {
                                    const range = tempSliderRanges[filter.key] ?? [filter.min, filter.max];
                                    const isDate = filter.type === 'date';
                                    const isChecks = filter.type === 'checks';
                                    const isFilterExpanded = expandedFilterKeys.includes(filter.key);
                                    return (
                                        <div key={filter.key}>
                                            <div
                                                className="flex items-center justify-between mb-1 cursor-pointer"
                                                onClick={() => toggleFilterGroup(filter.key)}
                                            >
                                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                                    <Checkbox
                                                        id={`filter-${layerKey}-${filter.key}`}
                                                        checked={getFilterCheckState(filter)}
                                                        onCheckedChange={(val) => handleFilterHeaderToggle(filter, !!val)}
                                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                        className="shrink-0"
                                                    />
                                                    <Typography variant="b2" className="font-medium text-foreground break-words min-w-0">
                                                        {filter.overlay}
                                                    </Typography>
                                                </div>
                                                {isFilterExpanded
                                                    ? <ChevronUp size={16} className="text-muted-foreground shrink-0" />
                                                    : <ChevronDown size={16} className="text-muted-foreground shrink-0" />
                                                }
                                            </div>
                                            {isFilterExpanded && (isChecks ? (
                                                <div className="flex flex-col">
                                                    {Array.from(new Set((filter.categories ?? []).map((c: any) => c.value))).map((value: any) => {
                                                        const allValues = Array.from(new Set((filter.categories ?? []).map((c: any) => c.value)));
                                                        const selected: string[] = tempChecksState[filter.key] ?? allValues;
                                                        const isSelected = selected.includes(value);
                                                        const cat = (filter.categories ?? []).find((c: any) => c.value === value);
                                                        return (
                                                            <div
                                                                key={value}
                                                                className="flex items-center gap-2 pl-10 pr-4 py-2 cursor-pointer"
                                                                onClick={() => {
                                                                    const next = isSelected
                                                                        ? selected.filter((v) => v !== value)
                                                                        : Array.from(new Set([...selected, value]));
                                                                    setTempChecksState(prev => ({ ...prev, [filter.key]: next }));
                                                                }}
                                                            >
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    onCheckedChange={(checked) => {
                                                                        const next = checked
                                                                            ? Array.from(new Set([...selected, value]))
                                                                            : selected.filter((v) => v !== value);
                                                                        setTempChecksState(prev => ({ ...prev, [filter.key]: next }));
                                                                    }}
                                                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                                    className="shrink-0"
                                                                />
                                                                <Typography variant="b2" className="text-muted-foreground break-words min-w-0 flex-1">
                                                                    {value}{cat?.count !== undefined ? ` (${cat.count})` : ''}
                                                                </Typography>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : isDate ? (
                                                <div className="flex flex-col gap-2 w-full">
                                                    <div className="flex flex-col gap-1">
                                                        <Typography variant="b3" className="text-muted-foreground text-[0.625rem]">From</Typography>
                                                        <input
                                                            type="date"
                                                            min={timestampToDateString(filter.min)}
                                                            max={timestampToDateString(range[1])}
                                                            value={timestampToDateString(range[0])}
                                                            onChange={(e) => {
                                                                const val = e.target.value ? new Date(e.target.value).getTime() : filter.min;
                                                                setTempSliderRanges(prev => ({
                                                                    ...prev,
                                                                    [filter.key]: [val, Math.max(range[1], val)],
                                                                }));
                                                            }}
                                                            className="w-full border border-border rounded-[var(--semantic-rounded-md)] px-3 py-1.5 text-xs font-sans text-foreground bg-background focus:outline-none focus:ring-1 focus:ring-brand"
                                                        />
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <Typography variant="b3" className="text-muted-foreground text-[0.625rem]">To</Typography>
                                                        <input
                                                            type="date"
                                                            min={timestampToDateString(range[0])}
                                                            max={timestampToDateString(filter.max)}
                                                            value={timestampToDateString(range[1])}
                                                            onChange={(e) => {
                                                                const val = e.target.value ? new Date(e.target.value).getTime() : filter.max;
                                                                setTempSliderRanges(prev => ({
                                                                    ...prev,
                                                                    [filter.key]: [Math.min(range[0], val), val],
                                                                }));
                                                            }}
                                                            className="w-full border border-border rounded-[var(--semantic-rounded-md)] px-3 py-1.5 text-xs font-sans text-foreground bg-background focus:outline-none focus:ring-1 focus:ring-brand"
                                                        />
                                                    </div>
                                                    <div className="flex justify-end">
                                                        <Checkbox
                                                            label="Same as 'From' date"
                                                            checked={range[1] === range[0]}
                                                            onCheckedChange={(checked) =>
                                                                setTempSliderRanges(prev => ({
                                                                    ...prev,
                                                                    [filter.key]: [range[0], checked ? range[0] : filter.max],
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <Slider
                                                    min={filter.min}
                                                    max={filter.max}
                                                    value={range}
                                                    onValueChange={([newMin, newMax]: [number, number]) =>
                                                        setTempSliderRanges(prev => ({ ...prev, [filter.key]: [newMin, newMax] }))
                                                    }
                                                    showMinMax
                                                    size="sm"
                                                />
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        </div>{/* end scrollable body */}

                        {/* add filter */}
                        <div className="border-t px-4 py-2.5 shrink-0">
                            <button
                                type="button"
                                disabled={layerFilters.length === 0}
                                className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#EDF2F9] py-2 text-[#000000] disabled:opacity-40 disabled:cursor-not-allowed"
                                onClick={() => {
                                    handleListOpen('categories');
                                    setShowMarkerFilterModal(true);
                                }}
                            >
                                <AddAlt size={16} />
                                <Typography variant="b2">Add Filter</Typography>
                            </button>
                        </div>

                        {/* footer */}
                        <div className="flex items-center justify-end gap-2 border-t px-4 py-2.5 shrink-0">
                            <Button variant="outline" size="small" onClick={handleCancelFilter}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                size="small"
                                onClick={handleApplyFilter}
                                disabled={selTempCats.category.length === 0 && selTempCats.subcategory.length === 0}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>,
                    document.body
                )
            }

            {/* Add Filter modal — rendered outside the portal so Dialog stacking works */}
            <MarkerFilterModal
                isOpen={showMarkerFilterModal}
                onClose={() => setShowMarkerFilterModal(false)}
                onConfirm={() => handleListOpen('categories')}
                layerKey={layerKey}
                layerTitle={layerTitle}
            />
        </div>
    );
};
